<template>
  <div
    class="flex cursor-pointer items-center whitespace-nowrap rounded px-2 py-1 transition *:w-full hover:bg-gray-200"
    @click.stop
  >
    <LexmeaCheckbox
      :modelValue="model"
      :indeterminate
      :label="item.title"
      :value="item"
      @update:modelValue="onModelUpdate($event as FilterMenuValue<T>[])"
    >
      <component
        :is="item.icon"
        v-if="isIconType(item)"
        :class="item.className || 'mr-2 h-4 w-4 shrink-0'"
        :colorClass="item.colorClass"
      />
      <span
        class="whitespace-nowrap p-1"
        :class="{ 'text-sm': small, 'font-semibold': bold }"
      >
        <slot>
          {{ item.title }}
        </slot>
      </span>
      <span
        v-if="$slots.count"
        class="ml-auto mt-1 w-fit pl-2 text-xs text-gray-500"
      >
        <slot name="count" :item="item"></slot>
      </span>
    </LexmeaCheckbox>
  </div>
</template>

<script lang="ts" setup generic="T">
import type {
  FilterMenuValue,
  FilterMenuValueWithIcon,
} from "./FilterMenuTypes";
import { traverseItems } from "./traverseItems";

type Props = {
  item: FilterMenuValue<T>;
  modelValue: FilterMenuValue<T>[];
  small?: boolean;
  bold?: boolean;
};

type Emits = {
  "update:modelValue": [checkedItems: Props["modelValue"]];
};

const { small = false, bold = false, item } = defineProps<Props>();
defineEmits<Emits>();

const model = defineModel<Props["modelValue"]>({
  required: true,
});

function isIconType(item: unknown): item is FilterMenuValueWithIcon {
  return Boolean("icon" in (item as FilterMenuValueWithIcon));
}

const compare = (a: Props["item"]) => (b: Props["item"]) =>
  a.key && b.key ? a.key === b.key : JSON.stringify(a) === JSON.stringify(b);

const counts = computed(() => {
  let total = 0;
  let selectedCount = 0;
  traverseItems([item], ({ item }) => {
    if (item.heading) return;
    total++;
    if (model.value.find(compare(item))) {
      selectedCount++;
    }
  });
  return {
    total,
    selectedCount,
  };
});

const indeterminate = computed(() => {
  const { total, selectedCount } = counts.value;
  return !item.heading && selectedCount > 0 && selectedCount < total;
});

watchEffect(() => {
  if (!item.children) return;

  const { total, selectedCount } = counts.value;

  if (total - 1 === selectedCount) {
    const index = model.value.findIndex(compare(item));
    if (index > -1) {
      model.value = model.value.filter((_, i) => i !== index);
    } else {
      model.value.push(item);
    }
  }
});

const onModelUpdate = (newModelValue: Props["modelValue"]) => {
  const newValue = newModelValue;
  if (item.children) {
    const wasAdded = newModelValue.find(compare(item));
    const pushToNewValue = (i: Props["item"]) => {
      if (!newValue.find(compare(i))) {
        newValue.push(i);
      }
    };
    const removeFromNewValue = (i: Props["item"]) => {
      const index = newValue.findIndex(compare(i));
      if (index !== -1) {
        newValue.splice(index, 1);
      }
    };
    const operation = wasAdded ? pushToNewValue : removeFromNewValue;
    traverseItems(item.children, ({ item }) => {
      if (item.heading) return;
      operation(item);
    });
  }

  model.value = newValue;
};
</script>
